import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/lib/media";
import { MaskReveal, Reveal } from "@/components/motion/Reveal";

const CHAPTERS = [
  {
    no: "01",
    title: "Trained Percussionist",
    body: "Classically trained on drums and percussion, DaniQ turns rhythm into a physical, electric language. Every set is anchored in real, live groove — not just a laptop.",
  },
  {
    no: "02",
    title: "DJ + Live Drummer",
    body: "She fuses precision DJ mixing with live drumming on top — layering acoustic energy over electronic sound so the room feels every kick, snare and roll in the moment.",
  },
  {
    no: "03",
    title: "MC & Showwoman",
    body: "With a theatrical background, DaniQ commands the crowd as an MC and performer — a full sensory show engineered to move rooms from lounge to mainstage.",
  },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  return (
    <section id="about" ref={ref} className="relative py-28 lg:py-40 px-6 lg:px-16 max-w-[1400px] mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left: text */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.4em] text-gold">The Story</span>
          </Reveal>
          <h2 className="mt-4 font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-wide text-white">
            <MaskReveal>MORE THAN</MaskReveal>
            <MaskReveal delay={0.1} className="text-magenta">A DJ SET</MaskReveal>
          </h2>

          <div className="mt-14 flex flex-col gap-12">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.08} className="group border-t border-white/10 pt-6">
                <div className="flex items-start gap-6">
                  <span className="font-display text-4xl text-white/25 group-hover:text-magenta transition-colors duration-500">{c.no}</span>
                  <div>
                    <h3 className="font-display text-3xl lg:text-4xl tracking-[0.1em] text-white">{c.title}</h3>
                    <p className="mt-3 max-w-xl text-white/60 leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Right: sticky parallax image */}
        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="lg:sticky lg:top-24 relative aspect-[3/4] overflow-hidden rounded-2xl">
            <motion.img
              style={{ y: imgY, scale: 1.15 }}
              src={IMAGES.aboutDrummer}
              alt="DaniQ live drumming"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-2xl" />
            <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-ink-950/90 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <p className="font-display text-2xl tracking-[0.2em] text-white">HANDS ON THE KIT</p>
              <p className="text-xs uppercase tracking-[0.3em] text-gold">Live · Every Show</p>
            </div>
            <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-violet/40 blur-[80px]" />
          </div>
        </div>
      </div>
    </section>
  );
};
